import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PartWrapper from '/react4xp/mixins/ui/part-wrapper';
import { Card, Col, Form, Input, Row, Select, Space } from 'antd';
import Details from '/react4xp/parts/reference-book-payment-details/components/details';
import { BankDetails, BeneficiaryDetails, Region } from '/react4xp/parts/reference-book-payment-details/reference-book-payment-details.types';
import { getBankDetails, getBeneficiaries, getRegions } from '/react4xp/parts/reference-book-payment-details/actions';
import Table, { ColumnsType } from 'antd/es/table';
import { localize } from '/react4xp/mixins/helpers';
import Button from '/react4xp/mixins/ui/button/button';
import { fields } from '/react4xp/parts/reference-book-payment-details/reference-book-payment-details.constants';

const ReferenceBookPaymentDetails = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [activeBeneficiaryCode, setActiveBeneficiaryCode] = useState<string>();
    const [beneficiaries, setBeneficiaries] = useState<BeneficiaryDetails[]>(null);
    const [bankDetails, setBankDetails] = useState<BankDetails>(null);
    const [regions, setRegions] = useState<Region[]>(null);

    const { translations } = useContext(ComponentContext);

    const [form] = Form.useForm();

    const columns: ColumnsType<BeneficiaryDetails> = useMemo(
        () =>
            fields.map((field) => ({
                key: field,
                title: localize(field, translations),
                dataIndex: field,
            })),
        []
    );

    useEffect(() => {
        setIsLoading(true);
        Promise.all([getBankDetails(), getRegions()])
            .then(([bankDetails, regions]) => {
                setBankDetails(bankDetails);
                setRegions(regions);
            })
            .finally(() => setIsLoading(false));
    }, []);

    const fetchBeneficiaries = useCallback(
        (pageNumber = 1) => {
            setIsLoading(true);
            setActiveBeneficiaryCode(null);
            getBeneficiaries(form.getFieldsValue(), pageNumber - 1)
                .then(setBeneficiaries)
                .finally(() => setIsLoading(false));
        },
        [form]
    );

    useEffect(() => {
        fetchBeneficiaries(pageNumber - 1);
    }, [pageNumber, fetchBeneficiaries]);

    const handlePageChange = useCallback((page: number) => {
        setPageNumber(page);
    }, []);

    const details = useMemo(
        () =>
            activeBeneficiaryCode
                ? {
                      ...bankDetails,
                      ...beneficiaries.find((b) => b.beneficiaryCode === activeBeneficiaryCode),
                  }
                : null,
        [activeBeneficiaryCode, beneficiaries, bankDetails]
    );

    const handleResetFilters = useCallback(() => {
        form.resetFields();
        fetchBeneficiaries();
    }, []);

    const handleDownload = () => {};

    return (
        <div className="half-back">
            <div className="container">
                <Card>
                    <Row
                        gutter={[16, 16]}
                        style={{ marginBottom: '1rem' }}
                        align="stretch"
                    >
                        <Col
                            sm={12}
                            xs={24}
                        >
                            <Form
                                onFinish={() => fetchBeneficiaries()}
                                form={form}
                                labelWrap
                                labelCol={{ span: 12 }}
                                layout="horizontal"
                            >
                                <Form.Item
                                    name="departmentCode"
                                    label={localize('departmentCode', translations)}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    name="beneficiaryCode"
                                    label={localize('beneficiaryCode', translations)}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    name="region"
                                    label={localize('region', translations)}
                                >
                                    <Select
                                        options={regions && regions?.map((region) => ({
                                            value: region.name,
                                            label: region.name,
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="beneficiaryName"
                                    label={localize('beneficiaryName', translations)}
                                >
                                    <Input />
                                </Form.Item>
                                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        loading={isLoading}
                                        onClick={handleResetFilters}
                                    >
                                        Сбросить
                                    </Button>
                                    <Button
                                        htmlType="submit"
                                        loading={isLoading}
                                        color="gold"
                                    >
                                        Поиск
                                    </Button>
                                </Space>
                            </Form>
                        </Col>
                        <Col
                            sm={12}
                            xs={24}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: 'column',
                                gap: 16,
                            }}
                        >
                            {details ? (
                                <Details details={details} />
                            ) : (
                                <Card title={localize('results.empty-title', translations)}>
                                    {localize('results.empty-description', translations)}
                                </Card>
                            )}
                            <Button
                                block
                                onClick={handleDownload}
                            >
                                {localize('cta.download-all', translations)}
                            </Button>
                        </Col>
                    </Row>
                    {beneficiaries && (
                        <Table
                            columns={columns}
                            dataSource={beneficiaries}
                            rowKey={'departmentCode'}
                            loading={isLoading}
                            pagination={{
                                current: pageNumber,
                                onChange: handlePageChange,
                            }}
                            bordered
                            scroll={{
                                x: 'max-content',
                            }}
                            onRow={(record) => {
                                return {
                                    onClick: () => {
                                        setActiveBeneficiaryCode(record.beneficiaryCode);
                                    },
                                };
                            }}
                        />
                    )}
                </Card>
            </div>
        </div>
    );
};

export const ComponentContext = createContext(null);

export default (props) => (
    <PartWrapper {...props}>
        <ComponentContext.Provider
            value={{
                language: props.language,
                commonTranslations: props.commonTranslations,
                translations: props.translations,
            }}
        >
            <ReferenceBookPaymentDetails />
        </ComponentContext.Provider>
    </PartWrapper>
);
